import React from 'react';

import { WithNAFallback } from '~/components/table';

import {
  PriceChangesChartLine,
  PriceChangesTooltipData,
  PriceChangesTooltipSort,
} from '../../PriceChangesChart.types';
import * as S from './PriceChangesChartTooltip.styles';
import { formatDate, getValueColor } from './utils';

type PriceChangesChartTooltipProps = {
  lines: PriceChangesChartLine[];
  tooltipData: PriceChangesTooltipData;
  formatter: (n: number) => string;
  sortBy?: PriceChangesTooltipSort;
  tooltipSubtitle?: string;
  locale?: string;
};

export const PriceChangesChartTooltip = ({
  lines,
  tooltipData,
  formatter,
  sortBy,
  tooltipSubtitle,
  locale = navigator.language,
}: PriceChangesChartTooltipProps) => {
  const isMultiAsset = lines.length > 1;

  const tooltipRows = lines.map(line => ({
    color: line.color,
    label: line.label,
    value: line.data.find(d => d.date.valueOf() === tooltipData.date.valueOf())
      ?.value,
    isBenchmark: line.isBenchmark,
  }));

  if (sortBy) {
    tooltipRows.sort((a, b) =>
      sortBy === 'desc'
        ? (b.value ?? 0) - (a.value ?? 0)
        : (a.value ?? 0) - (b.value ?? 0)
    );
  }

  const getTooltipContent = () => (
    <S.TooltipLabelContainer
      $isMultiAsset={isMultiAsset}
      data-testid="price-changes-chart-tooltip"
    >
      <S.DateRow>
        <S.DateLabel>{formatDate(tooltipData.date, locale)}</S.DateLabel>
        {isMultiAsset && tooltipSubtitle && (
          <S.DateSubtitle>{tooltipSubtitle}</S.DateSubtitle>
        )}
      </S.DateRow>
      {tooltipRows.map((row, index) => (
        <S.TooltipRow key={row.label || index} data-testid="tooltip-row">
          <S.TooltipLegend>
            {isMultiAsset && (
              <S.TooltipLegendColor
                $color={row.color}
                $dashed={row.isBenchmark}
              />
            )}
            <S.TooltipLabel>{row.label}</S.TooltipLabel>
          </S.TooltipLegend>
          <S.TooltipValueArea data-testid="row-value-label">
            <WithNAFallback
              value={
                row.value !== undefined ? (
                  <S.TooltipValue
                    $color={getValueColor(isMultiAsset, row.value)}
                  >
                    {`${isMultiAsset && row.value > 0 ? '+' : ''}${formatter(
                      row.value
                    )}`}
                  </S.TooltipValue>
                ) : null
              }
              check={row.value !== undefined}
            />
          </S.TooltipValueArea>
        </S.TooltipRow>
      ))}
    </S.TooltipLabelContainer>
  );

  return (
    <S.ChartTooltip
      inPortal
      label={getTooltipContent()}
      isTouchDevice={false}
      isOpen
      hasArrow={false}
      dismissible={false}
      strategy="fixed"
      placement="bottom"
    >
      <S.TooltipRoot style={{ left: `${tooltipData.xPos}px` }} />
    </S.ChartTooltip>
  );
};
